/**
 * How reachable a system is from untrusted networks, as one vocabulary.
 *
 * Pure on purpose, so an aggregate can classify exposure without pulling a React module in to do it.
 *
 * On this branch `coerceInternetFacing` is the only symbol wired up — `risk-from-scorecard.ts` weights the
 * score by it. `INTERNET_FACING` and `isReachableFromUntrusted` are here for the org-level roll-up on
 * ISS-8555, which grew its own bare `Set(['yes', 'partial'])` that disagreed with this vocabulary about
 * unrecognised values; that branch adopts these so the estate figure and the repository chip cannot
 * answer differently. Until it lands, they have no consumer here beyond their tests.
 */

/**
 * The categorical fields arrive as free text, not enums.
 *
 * `api-schemas.ts` types `trust_level`, `relevance` and `internet_facing` as `z.string()` on purpose:
 * "the values are generated per model and the UI degrades gracefully on ones it does not recognise".
 * Callers therefore normalise and fall back rather than indexing a recipe with an unknown key, which
 * would render an unstyled tag. The fallbacks are the least-committal value in each vocabulary — an
 * unrecognised value must never read as a stronger claim than the model made.
 *
 * Used by the trust-level and relevance coercers in `threat-model-report.tsx` too, which bucket their own
 * vocabularies the same way. That was written as already true while they still held a verbatim copy — the
 * duplication this module exists to prevent, one level down. They import it now.
 */
export const normalise = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')

/**
 * Ordered most to least exposed, then `unknown`, the way `trust-levels.ts` orders its ramp: anything
 * presenting these as a series — a composition bar, a legend — can map over this and get a meaningful
 * order for free rather than hand-sorting a copy that later disagrees.
 */
export const INTERNET_FACING = ['yes', 'partial', 'no', 'unknown'] as const
export type InternetFacing = (typeof INTERNET_FACING)[number]

export const coerceInternetFacing = (value: string): InternetFacing =>
  INTERNET_FACING.find(option => option === normalise(value)) ?? 'unknown'

/**
 * `partial` counts as reachable.
 *
 * The models hedge — a `partial` carries notes like "exposure depends on deployment" — and a figure that
 * counted only `yes` would report a boundary the model never claimed. `unknown` does not count: absence
 * of an answer is not an answer, and treating it as reachable would inflate every exposure figure with
 * repositories nobody has assessed.
 */
const REACHABLE_FROM_UNTRUSTED: readonly InternetFacing[] = ['yes', 'partial']

export const isReachableFromUntrusted = (value: InternetFacing): boolean => REACHABLE_FROM_UNTRUSTED.includes(value)
